import { getCookie, getCookieDomain, generateUUID } from '../utility/index.js';

/**
 * UTM Cookies Management
 * Handles UTM parameter storage and link parameter propagation to the grow app
 */

/**
 * Sync UUIDs between different tracking systems
 */
const syncUUID = () => {
	const statsigCookieKey = 'statsig_stable_id';
	const localStorageKey = 'STATSIG_LOCAL_STORAGE_STABLE_ID';

	// Set cookie expiration (1 year from now)
	const date = new Date();
	date.setFullYear(date.getFullYear() + 1);
	const expires = `expires=${date.toUTCString()}`;

	const currentCookieValue = getCookie(statsigCookieKey);
	const currentLocalStorageValue = localStorage?.getItem(localStorageKey);

	if (currentCookieValue) {
		localStorage.setItem(localStorageKey, currentCookieValue);
	} else if (currentLocalStorageValue) {
		document.cookie = `${statsigCookieKey}=${currentLocalStorageValue}; ${expires}; path=/; domain=${getCookieDomain()}`;
	} else {
		const newUUID = generateUUID();
		localStorage?.setItem(localStorageKey, newUUID);
		document.cookie = `${statsigCookieKey}=${newUUID}; ${expires}; path=/; domain=${getCookieDomain()}`;
	}
};

/**
 * UTM Cookies Manager Class
 */
export default class UTMCookiesManager {
	constructor() {
		// Also read server-side as grow_{param} cookies by includes/gravity-forms.php for screener webhooks
		this.ANALYTICS_PARAMS = [
			'utm_campaign',
			'utm_source',
			'utm_medium',
			'utm_content',
			'utm_term',
			'utm_device',
			'network',
			'placement',
			'geoid',
			'matchtype',
			'gclid',
			'fbclid',
			'ndclid',
			'msclkid',
			'g_campaign',
			'g_campaignid',
		];

		this.FIRST_TOUCH_ANALYTICS_PARAMS = [
			'utm_campaign',
			'utm_source',
			'utm_medium',
			'utm_content',
			'utm_term',
			'utm_device',
		];

		this.init();
	}

	/**
	 * Setup UTM cookies from URL parameters
	 */
	init() {
		const urlParams = new URLSearchParams(window.location.search);
		const utmParams = Array.from(urlParams.entries())
			.filter(([key]) => key.startsWith('utm_') || key.startsWith('cc_opt'))
			.map(([key, value]) => `${key}=${value}`)
			.join('&');

		// Set cookie expiration (1 year from now)
		const date = new Date();
		date.setFullYear(date.getFullYear() + 1);
		const expires = `expires=${date.toUTCString()}`;

		// Store analytics parameters
		this.ANALYTICS_PARAMS.forEach((param) => {
			const value = urlParams.get(param);
			if (value) {
				document.cookie = `grow_${param}=${value}; ${expires}; domain=${getCookieDomain()}; path=/`;
			}
		});

		// Store first-touch analytics parameters (only if not already set)
		this.FIRST_TOUCH_ANALYTICS_PARAMS.forEach((param) => {
			const value = urlParams.get(param);
			if (value) {
				const firstKey = `grow_first_${param}`;
				if (
					!document.cookie.split('; ').find((row) => row.startsWith(firstKey))
				) {
					document.cookie = `${firstKey}=${value}; ${expires}; domain=${getCookieDomain()}; path=/`;
				}
			}
		});

		// Setup link parameter handling
		this.setupLinkHandling(utmParams);

		// Sync UUIDs
		syncUUID();
	}

	/**
	 * Setup link parameter handling
	 * @param {string} utmParams - UTM parameters string
	 */
	setupLinkHandling(utmParams) {
		const links = document.querySelectorAll('a');
		links.forEach((link) => {
			link.addEventListener('click', () => {
				this.handleUtmParams(link, utmParams);
				this.handleParamsOnNavigation(link);
			});
		});
	}

	/**
	 * Handle UTM parameters for links
	 * @param {HTMLAnchorElement} link      - Link element
	 * @param {string}            utmParams - UTM parameters string
	 */
	handleUtmParams(link, utmParams) {
		if (link.href && !link.href.includes(utmParams)) {
			if (!link.href.includes('#')) {
				const url = new URL(link.href, window.location.href);
				url.search += (url.search ? '&' : '?') + utmParams;
				link.href = url.toString();
			}
		}
	}

	/**
	 * Handle appending url parameters when navigating from the marketing site
	 * to the grow app (e.g. /filters, /therapists/*, /categories*)
	 * @param {HTMLAnchorElement} link - Link element
	 */
	handleParamsOnNavigation(link) {
		const { search } = window.location;
		if (!search) return;

		const href = link.getAttribute('href');
		if (!href) return;

		// Process only links that navigate to the grow app
		if (
			!href.startsWith(`${window.location.origin}/filters`) &&
			!href.startsWith(`${window.location.origin}/therapists`) &&
			!href.startsWith(`${window.location.origin}/categories`)
		) {
			return;
		}

		const url = new URL(href, window.location.origin);
		const urlParams = new URLSearchParams(url.search);
		const currentParams = new URLSearchParams(search);

		// If both the current url and the link has a value set for the same key
		// This will prioritize the value set on the link
		currentParams.forEach((value, key) => {
			if (!urlParams.has(key)) {
				urlParams.append(key, value);
			}
		});

		url.search = urlParams.toString();
		link.setAttribute('href', url.toString());
	}
}
